import React, { useState } from 'react';
import '../styles/onboard.css';
import OnboardLeftSection from '../components/OnboardLeftSection';
import { isValidEmail, getEmailValidationError } from '../utils/validation';
import { logErrorInDev } from '../utils/errorLogger';
import { FaEnvelope } from 'react-icons/fa';
import { supabase } from '../integrations/client.ts';

function ForgotPassword({ onClose, onSwitchToSignIn }) {
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleEmailChange = (e) => {
        setEmail(e.target.value);
        if (error) setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        // Validation
        const validationError = getEmailValidationError(email);
        if (validationError) {
            setError(validationError);
            setLoading(false);
            return;
        }

        try {
            // Use environment variable for production URL, fallback to window.location.origin for dev
            const appUrl = import.meta.env.VITE_APP_URL || window.location.origin;

            // Use Supabase's built-in password reset method
            const { error } = await supabase.auth.resetPasswordForEmail(email, {
                redirectTo: `${appUrl}/reset-password`
            });

            if (error) {
                throw error;
            }

            setSuccess(true);
        } catch (err) {
            // Log error in development mode only
            logErrorInDev('ForgotPassword', err);

            // Show user-friendly message
            let errorMessage = 'Failed to send reset email. Please try again.';

            if (err.message && err.message.includes('Invalid email')) {
                errorMessage = 'Please enter a valid email address.';
            } else if (err.message && err.message.includes('rate limit')) {
                errorMessage = 'Please wait a moment before requesting another reset email.';
            } else if (err.message && err.message.includes('network')) {
                errorMessage = 'Network error. Please check your connection and try again.';
            }

            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleSignInClick = () => {
        if (onSwitchToSignIn) {
            onSwitchToSignIn();
        }
    };

    if (success) {
        return (
            <div className="onboard-container signin-container">
                <div className="signin-layout">
                    <OnboardLeftSection
                        onClose={onClose}
                        loading={false}
                        mode="forgotPassword"
                    />

                    <div className="signin-content">
                        <div style={{ textAlign: 'center', padding: '40px 0' }}>
                            <div style={{
                                width: '80px',
                                height: '80px',
                                margin: '0 auto 30px',
                                background: 'transparent',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '40px',
                                border: 'none'
                            }}>
                                <FaEnvelope style={{ color: '#000000', fontSize: '40px' }} />
                            </div>
                            <h1>Check Your Email</h1>
                            <p className="signin-subtitle" style={{ maxWidth: '500px', margin: '20px auto' }}>
                                We've sent a password reset link to <strong>{email}</strong>
                            </p>
                            <p style={{ color: '#666', marginTop: '20px', fontSize: '14px' }}>
                                The link will expire in 1 hour for security reasons.
                            </p>

                            <div style={{
                                backgroundColor: '#F5F3ED',
                                padding: '20px',
                                borderRadius: '8px',
                                marginTop: '30px',
                                textAlign: 'left'
                            }}>
                                <p style={{ margin: '0 0 10px 0', color: '#333', fontWeight: '600' }}>
                                    Didn't receive the email?
                                </p>
                                <ul style={{ margin: '10px 0', paddingLeft: '20px', color: '#666', fontSize: '14px', lineHeight: '1.8' }}>
                                    <li>Check your spam or junk folder</li>
                                    <li>Make sure you entered the correct email address</li>
                                    <li>Wait a few minutes for the email to arrive</li>
                                </ul>
                                <button
                                    onClick={() => setSuccess(false)}
                                    style={{
                                        marginTop: '15px',
                                        padding: '10px 20px',
                                        background: 'transparent',
                                        border: '2px solid #333333',
                                        borderRadius: '6px',
                                        color: '#333333',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        fontSize: '14px'
                                    }}
                                >
                                    Try Another Email
                                </button>
                            </div>

                            <div className="signin-footer" style={{ marginTop: '40px' }}>
                                <p>
                                    Remember your password?{' '}
                                    <span className="signup-link" onClick={handleSignInClick}>
                                        Sign in here
                                    </span>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="onboard-container signin-container">
            <div className="signin-layout">
                <OnboardLeftSection
                    onClose={onClose}
                    loading={loading}
                    mode="forgotPassword"
                />

                <div className="signin-content">
                    <h1>Reset Your Password</h1>
                    <p className="signin-subtitle">
                        Enter your email address and we'll send you a link to reset your password
                    </p>

                    <form className="signin-form" onSubmit={handleSubmit}>
                        {error && (
                            <div className="error-message" style={{
                                color: '#e74c3c',
                                backgroundColor: '#fdf2f2',
                                padding: '10px',
                                borderRadius: '4px',
                                marginBottom: '15px',
                                border: '1px solid #fecaca'
                            }}>
                                {error}
                            </div>
                        )}

                        <div className="form-group">
                            <label htmlFor="email">Email Address</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={email}
                                onChange={handleEmailChange}
                                placeholder="Enter your email"
                                required
                                autoFocus
                            />
                        </div>

                        <button type="submit" className="signin-btn" disabled={loading}>
                            {loading ? 'Sending Reset Link...' : 'Send Reset Link'}
                        </button>
                    </form>

                    <div className="signin-footer">
                        <p>
                            Remember your password?{' '}
                            <span className="signup-link" onClick={handleSignInClick}>
                                Sign in here
                            </span>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ForgotPassword;
